/**
 * Queue + now-playing data hooks.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { nowPlayingApi, queueApi } from "../lib/api";
import { finalizeActiveListen } from "../lib/finalizeListen";
import { usePlayer } from "../state/player";

export const QUEUE_KEY = ["queue"] as const;
export const NOW_PLAYING_KEY = ["now-playing"] as const;

export function useQueue() {
  return useQuery({
    queryKey: QUEUE_KEY,
    queryFn: () => queueApi.get().then((r) => r.queue),
    staleTime: 2_000,
  });
}

export function usePutQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      queue: { id: number; durationSec?: number }[];
      currentIndex?: number;
      currentTime?: number;
    }) => queueApi.put(params.queue, params.currentIndex, params.currentTime),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUEUE_KEY }),
  });
}

export function useClearQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await finalizeActiveListen();
      await queueApi.clear();
      usePlayer.getState().clearQueueLocal();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUEUE_KEY });
      qc.invalidateQueries({ queryKey: NOW_PLAYING_KEY });
    },
  });
}

export function useNowPlaying() {
  return useQuery({
    queryKey: NOW_PLAYING_KEY,
    queryFn: () => nowPlayingApi.get().then((r) => r.nowPlaying),
    staleTime: 1_000,
  });
}

export function useSetNowPlaying() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; index: number; queueLength: number; durationSec?: number }) =>
      nowPlayingApi.set(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOW_PLAYING_KEY }),
  });
}

export function useClearNowPlaying() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fullClear: boolean) => nowPlayingApi.clear(fullClear),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOW_PLAYING_KEY }),
  });
}
